import { Schema } from "effect";
import type { Finding } from "./finding.ts";

// U6 (severity buckets replace the rubric score and the run matrix): a document's quality readout
// is a count of open findings per severity bucket, drawn from lint and manual findings alike.
// This module maps lint's two-value `fatal|warning` (severity.ts) onto the `blocker|warning|info`
// buckets the wire `DiagnosticAnchor.severity` carries.
//
// `FindingSeverity` is the canonical wire vocabulary, defined once here (a leaf module with no
// downstream deps) and imported everywhere it is needed on a schema — `collab.ts`'s
// `DiagnosticAnchor.severity`, `audit.ts`/`tool-args.ts`'s manual-finding stamped `severity` —
// rather than re-inlined at each site: unlike `DiagnosticAnchor.author` (inlined in `collab.ts`
// specifically to avoid a cycle with `audit.ts`), nothing here creates one.
export const FindingSeverity = Schema.Literal("blocker", "warning", "info");
export type FindingSeverity = typeof FindingSeverity.Type;

export type FindingKind = "manual" | "lint";

// A manual finding as this module needs it: an already-resolved severity — stamped by the writer
// when present (`annotation.created.v2`'s additive `severity` field), or defaulted by
// `review-state.ts`'s `deriveSeverity` when absent. This module never re-derives it.
export interface OpenManualFinding {
  readonly severity: FindingSeverity;
}

const SeverityCounts = Schema.Struct({
  blocker: Schema.Int.pipe(Schema.nonNegative()),
  warning: Schema.Int.pipe(Schema.nonNegative()),
  info: Schema.Int.pipe(Schema.nonNegative()),
});
type SeverityCounts = typeof SeverityCounts.Type;
// The fold's working copy: `Schema.Struct`'s derived type is `readonly`, but the count-and-bump loop
// below mutates in place (one pass over both finding lists, not rebuilt objects).
type MutableSeverityCounts = { -readonly [K in keyof SeverityCounts]: SeverityCounts[K] };

export const FindingSummary = Schema.Struct({
  // Open findings, all kinds, by severity.
  counts: SeverityCounts,
  byKind: Schema.Struct({ manual: SeverityCounts, lint: SeverityCounts }),
  // === counts.blocker, surfaced separately on purpose (ADR-0057: a blocked curriculum must never
  // read near-perfect) — naming it in the type is what stops a consumer summing `counts` and losing
  // that property.
  blockers: Schema.Int.pipe(Schema.nonNegative()),
});
export type FindingSummary = typeof FindingSummary.Type;

const emptyCounts = (): MutableSeverityCounts => ({ blocker: 0, warning: 0, info: 0 });

// The all-zero reading. `DiagnosticsPayload.findingSummary` is itself required (every frame this
// schema encodes carries one, so it needs no decode default) — this constant is the client's own
// initial/reset state instead: `apps/web/src/live-store.ts`'s `emptyLiveState` and its `snapshot`
// fold, which reads "nothing open yet" before the first `diagnostics` frame arrives.
export const emptyFindingSummary: FindingSummary = {
  counts: emptyCounts(),
  byKind: { manual: emptyCounts(), lint: emptyCounts() },
  blockers: 0,
};

// Lint's own two-value `Severity` means "is this document structurally valid", a different question
// from "how bad is this finding" — mapped at this projection boundary, not at lint's source
// (severity.ts stays `fatal|warning` throughout).
const lintSeverityToFindingSeverity = (severity: Finding["severity"]): FindingSeverity =>
  severity === "fatal" ? "blocker" : "warning";

export const findingSummary = (
  manual: readonly OpenManualFinding[],
  lint: readonly Finding[],
): FindingSummary => {
  const counts = emptyCounts();
  const byKind: Record<FindingKind, MutableSeverityCounts> = {
    manual: emptyCounts(),
    lint: emptyCounts(),
  };

  const bump = (kind: FindingKind, severity: FindingSeverity): void => {
    counts[severity] += 1;
    byKind[kind][severity] += 1;
  };

  for (const finding of manual) bump("manual", finding.severity);
  for (const finding of lint) bump("lint", lintSeverityToFindingSeverity(finding.severity));

  return { counts, byKind, blockers: counts.blocker };
};
